import './tracking-table.component.ajscss';

import {
  fromJson,
  IComponentController,
  IComponentOptions,
  IFilterService,
  ILogService,
  IParseService,
  isArray,
  ISCEService,
  isString,
} from 'angular';
import { i18n } from 'i18next';
import { DiffPatcher, formatters } from 'jsondiffpatch';

import { DisplayDeprecatedComponent, MessagingService } from '@contact/upgrade';

import { UserTrackingFunction } from '../models/tracking-function';
import { IUserTrackingService } from '../tracking.service';
import templateUrl from './tracking-table.component.ajs.html';

export const RESET_TRACKING_FILTER: string = 'RESET_TRACKING_SUB_FILTER';

class UserTrackingTableCtrl extends DisplayDeprecatedComponent implements IComponentController {
  public trackingFn: UserTrackingFunction;
  public detail: any = null;
  public filter: boolean = true;
  private basicColumns: Array<any> = [{ name: 'dateTime' }, { name: 'user' }, { name: 'object' }, { name: 'action' }];
  private agentColumns: Array<any> = [];
  private columns: Array<any> = [];
  public rows: Array<any> = [];
  private actionState = false;
  public actionStateClass = '';
  public actionPosition = 'left';
  public applicationColor = 'red';
  public actionInfoState = false;
  public columnFilterState = false;
  private oneSearchDone = false;
  public searchInProgress = false;
  private maxChecked = 10;
  private checked = 0;
  public mstep = 5;
  public ismeridian = false;
  public todayDateTime: Date;
  private minDate: Date;
  public maxDate: Date;
  private beginHours = 0;
  private beginMinutes = 0;
  private endHours = 0;
  private endMinutes = 0;
  private beginDateTime: Date = new Date();
  private endDateTime: Date = new Date();
  public dateOptions = {
    formatYear: 'yy',
    startingDay: 1,
  };
  public format = 'dd/MM/yyyy';
  private status = {
    openedBeginDate: false,
    openedEndDate: false,
  };
  public updateData = '';
  public updateTime = '';
  public updatedObject = '';
  public updateInitiator = '';
  public updateActionText = '';
  private extractUpdateDataId = this.$parse('updateDataId');

  constructor(
    private readonly $log: ILogService,
    private readonly $i18next: i18n,
    private readonly $filter: IFilterService,
    private readonly $parse: IParseService,
    private readonly $sce: ISCEService,
    private readonly $window: any,
    private readonly messaging: MessagingService,
    private readonly tracking: IUserTrackingService
  ) {
    super();
  }

  $onInit() {
    this.agentColumns = this.basicColumns.slice(0, 2);
    this.agentColumns.push({ name: 'activity' });
    this.agentColumns.push({ name: 'description' });
    this.columns = this.basicColumns;
    this.selectDefaultColumn();

    this.tracking
      .getUpdatedHistoryRetention()
      .then(retentionValue => {
        this.initDateTimes(retentionValue);
      })
      .then(() => {
        // uggly fix for calendar endate min not well valued from begindate
        setTimeout(() => {
          this.resetFilters();
        }, 2000);
      });

    this.changeView(this.trackingFn);
  }

  override onWatchSize(size: string) {
    if (size != null && this.$window != null) {
      if (size === 'xsmall') {
        this.moveAction('bottom');
      } else {
        this.moveAction('right');
      }
      switch (size) {
        case 'xsmall':
          this.maxChecked = this.$window.Math.min(2, this.columns.length);
          break;
        case 'small':
          this.maxChecked = this.$window.Math.min(4, this.columns.length);
          break;
        case 'medium':
          this.maxChecked = this.$window.Math.min(6, this.columns.length);
          break;
        case 'large':
          this.maxChecked = this.$window.Math.min(10, this.columns.length);
          break;
        default:
          this.maxChecked = this.$window.Math.min(5, this.columns.length);
          break;
      }
      this.selectDefaultColumn();
    }
  }

  public get currentFunction(): UserTrackingFunction {
    return this.tracking.getFunction();
  }

  private unwrapExtraData(data) {
    try {
      return fromJson(data);
    } catch (e) {
      return null;
    }
  }

  public buildObjectText(row): string {
    let objectText;
    let rowTargetLabel = '';
    let rowTargetType = '';
    if (row.hasOwnProperty('targetType')) {
      rowTargetType = '' + row.targetType;
    }
    if (row.hasOwnProperty('targetLabel')) {
      rowTargetLabel = '' + row.targetLabel;
    }
    switch (rowTargetType) {
      // specific cases => target object is CSR. use row target id to find CSR
      case 'CURRENT_CONTACT':
      case 'NEXT_CONTACT':
      case 'PAUSE':
      case 'ADD_SKILL':
      case 'REMOVE_SKILL':
        rowTargetType = 'CSR';
        rowTargetLabel = '';
        break;
      // specific cases => target object is CSR or USER according to view
      case 'USER_PLANNING':
      case 'PASSWORD':
        if (this.tracking.getFunction() === UserTrackingFunction.SUPERVISOR) {
          rowTargetType = 'CSR';
          rowTargetLabel = '';
        } else {
          rowTargetType = 'USER';
        }
        if (row.hasOwnProperty('targetLabel')) {
          rowTargetLabel = '' + row.targetLabel.replace('$', ' ');
        }
        break;
      // specific cases => target object is SKILL. use row target id to find skill
      case 'SKILL_PLANNING':
      case 'SKILL_WEIGHT':
        rowTargetType = 'SKILL';
        rowTargetLabel = '';
        break;
      // specific cases => target object is CHANNEL. use row target label to build channel label
      case 'CHANNEL_PLANNING':
        rowTargetType = 'DOMAIN';
        break;
      case 'CHANNEL':
        rowTargetType = 'CHANNEL';
        rowTargetLabel = this.$i18next.t('commons:basic.channels.' + rowTargetLabel);
        break;
      // specific cases => target object is ROUTING_STRATEGY. use row target label and change targetType
      case 'ROUTING_PLANNING':
        rowTargetType = 'ROUTING_STRATEGY';
        break;
      case 'DIRECTORY_CORRESPONDENT':
      case 'DIRECTORY_INTERNAL_CORRESPONDENT':
        let correspondentData = this.unwrapExtraData(row.extraData);
        if (correspondentData !== null && correspondentData.hasOwnProperty('correspondent')) {
          row.targetLabel = correspondentData.correspondent.firstName + ' ' + correspondentData.correspondent.lastName;
          rowTargetLabel = correspondentData.correspondent.firstName + ' ' + correspondentData.correspondent.lastName;
        }
        break;
      case 'ALERT_TEMPLATE':
        if (row.targetLabel != null) {
          let targetArray = row.targetLabel.split('%§%');
          if (targetArray.length > 1) {
            rowTargetType = targetArray[0];
            rowTargetLabel = targetArray[1];
            rowTargetLabel = rowTargetLabel.replace('%@%', ' ');
          } else {
            rowTargetLabel = '' + row.targetLabel.replace('%@%', ' ');
          }
        }
        break;
      case 'INSTRUCTION_CATEGORY':
        rowTargetLabel = '' + row.targetLabel.replace(/\$/g, ' ');
        break;
      default:
        break;
    }
    if (rowTargetType !== '') {
      if (rowTargetLabel !== '') {
        if (
          'DOMAIN_DIFFUSION' === rowTargetType ||
          'TARGET_DIFFUSION' === rowTargetType ||
          'INSTRUCTION_CATEGORY' === rowTargetType ||
          'HITCH' === rowTargetType ||
          'INSTRUCTION' === rowTargetType ||
          'INSTRUCTION_TEMPLATE' === rowTargetType ||
          'REMINDER' === rowTargetType ||
          'ROUTING_INDICATORS' === rowTargetType
        ) {
          objectText = this.$i18next.t('tracking:object.' + rowTargetType, { label: rowTargetLabel });
        } else if ('USER' !== rowTargetType) {
          objectText = this.$i18next.t('tracking:object.' + row.targetType, { label: rowTargetLabel });
        } else {
          objectText = this.$i18next.t('tracking:object.' + row.targetType, { label: rowTargetLabel.replace('$', ' ') });
        }
      } else {
        let newLabel;
        let entity;
        switch (rowTargetType) {
          case 'CSR':
            entity = this.tracking.findCsrFromUuid(row.targetId);
            if (entity !== null) {
              newLabel = entity.firstName + ' ' + entity.lastName;
            } else {
              newLabel = '';
            }
            break;
          case 'SKILL':
            entity = this.tracking.findSkillFromUuid(row.targetId);
            if (entity !== null) {
              newLabel = entity.name;
            } else {
              newLabel = '';
            }
            break;
          case 'USER':
            entity = this.tracking.findUserFromUuid(row.targetId);
            if (entity !== null) {
              newLabel = entity.firstName + ' ' + entity.lastName;
            } else {
              newLabel = '';
            }
            break;
          default:
            newLabel = '';
            break;
        }
        if (newLabel !== '') {
          objectText = this.$i18next.t('tracking:object.' + rowTargetType, { label: newLabel });
        } else if (rowTargetType !== '') {
          objectText = this.$i18next.t('tracking:object.' + rowTargetType, { label: this.$i18next.t('tracking:object.unknown') });
        } else {
          objectText = '-';
        }
      }
    } else {
      objectText = '-';
    }

    return objectText;
  }

  public buildActionText(row) {
    let params;
    switch (row.action + '_' + row.targetType) {
      case 'ADD_SKILL_CSR':
      case 'REMOVE_SKILL_CSR':
        let skillData = this.unwrapExtraData(row.targetId);
        if (skillData != null && skillData.hasOwnProperty('skillId') && skillData.hasOwnProperty('limitDate')) {
          let newLabel;
          let limitDate = this.$filter('date')(skillData.limitDate, 'HH:mm:ss');
          let entity = this.tracking.findSkillFromUuid(skillData.skillId);
          if (entity != null) {
            newLabel = entity.name;
          } else {
            newLabel = '';
          }

          params = { label: newLabel, limitDate: limitDate };
        }
        break;
      case 'ADD_POSITION_SITE':
      case 'DELETE_POSITION_SITE':
        let posData = this.unwrapExtraData(row.extraData);
        if (posData !== null && posData.hasOwnProperty('position')) {
          params = { label: posData.position };
        }
        break;
      case 'ADD_USER_TEAM':
      case 'DELETE_USER_TEAM':
      case 'ADD_USER_GROUP':
      case 'DELETE_USER_GROUP':
        let userData = this.unwrapExtraData(row.extraData);
        if (userData !== null && userData.hasOwnProperty('userId')) {
          let user = this.tracking.findUserFromUuid(userData.userId);
          if (user !== null) {
            params = { label: user.firstName + ' ' + user.lastName };
          }
        }
        break;
      case 'UPDATE_CHANNEL_PLANNING':
      case 'UPDATE_SKILL_PLANNING':
        let channelData = this.unwrapExtraData(row.extraData);
        if (channelData !== null && channelData.hasOwnProperty('channel')) {
          params = { label: this.$i18next.t('commons:basic.channels.' + channelData.channel) };
        }
        break;
      case 'UPDATE_RANK_ROUTING_STRATEGY':
        let behaviorsData = this.unwrapExtraData(row.extraData);
        if (behaviorsData !== null && behaviorsData.hasOwnProperty('BehaviorRank') && isArray(behaviorsData.BehaviorRank)) {
          let behaviorNames = '';
          let behaviorRanks = '';
          behaviorsData.BehaviorRank.forEach(behaviorRank => {
            if (behaviorNames === '') {
              behaviorNames = behaviorRank.label;
              behaviorRanks = behaviorRank.value;
            } else {
              behaviorRank.label += ', ' + behaviorRank.label;
              behaviorRanks += ', ' + behaviorRank.value;
            }
          });
          params = { behaviors: behaviorNames, ranks: behaviorRanks };
        }
        break;
      case 'CREATE_ROUTING_BEHAVIOR':
      case 'UPDATE_ROUTING_BEHAVIOR':
      case 'UPDATE_ROUTING_DEFAULT_BEHAVIOR':
      case 'DELETE_ROUTING_BEHAVIOR':
      case 'CREATE_ROUTING_INDICATORS':
      case 'DELETE_ROUTING_INDICATORS':
        // retrieve behavior name in diff
        let strategyData = this.unwrapExtraData(row.extraData);
        if (strategyData !== null && strategyData.hasOwnProperty('strategyName')) {
          params = { label: strategyData.strategyName };
        }
        break;
      case 'UPDATE_ACTIVATION_CHANNEL':
        let data = this.unwrapExtraData(row.extraData);
        if (
          data !== null &&
          data.hasOwnProperty('data') &&
          data.data.hasOwnProperty('domainName') &&
          data.data.hasOwnProperty('activation')
        ) {
          params = {
            domain: data.data.domainName,
            activation: this.$i18next.t('tracking:ADMINISTRATOR.activity.UPDATE_ACTIVATION_CHANNEL.' + data.data.activation),
          };
        }
        break;
      case 'CREATE_DIRECTORY_CATEGORY':
      case 'UPDATE_DIRECTORY_CATEGORY':
      case 'DELETE_DIRECTORY_CATEGORY':
        let directoryData = this.unwrapExtraData(row.extraData);
        if (directoryData !== null && directoryData.hasOwnProperty('directoryName')) {
          params = { label: directoryData.directoryName };
        }
        break;
      case 'CREATE_DIRECTORY_CORRESPONDENT':
      case 'IMPORT_DIRECTORY_CORRESPONDENT':
      case 'UPDATE_DIRECTORY_CORRESPONDENT':
      case 'DELETE_DIRECTORY_CORRESPONDENT':
        let corespondentData = this.unwrapExtraData(row.extraData);
        if (
          corespondentData !== null &&
          corespondentData.hasOwnProperty('correspondent') &&
          corespondentData.hasOwnProperty('directoryName')
        ) {
          if (corespondentData.hasOwnProperty('categoryName') && corespondentData.categoryName !== '') {
            params = {
              label: corespondentData.correspondent.firstName + ' ' + corespondentData.correspondent.lastName,
              category: corespondentData.categoryName,
              directory: corespondentData.directoryName,
            };
            return this.tracking.getActivityI18nText(
              this.tracking.getFunction(),
              'action.withCategory',
              row.action,
              row.targetType,
              params
            );
          } else {
            params = {
              label: corespondentData.correspondent.firstName + ' ' + corespondentData.correspondent.lastName,
              directory: corespondentData.directoryName,
            };
            return this.tracking.getActivityI18nText(
              this.tracking.getFunction(),
              'action.withoutCategory',
              row.action,
              row.targetType,
              params
            );
          }
        }
        break;
      case 'UPDATE_DIRECTORY_INTERNAL_CORRESPONDENT':
        let visibilityData = this.unwrapExtraData(row.extraData);
        if (visibilityData !== null && visibilityData.hasOwnProperty('visibility')) {
          params = {
            label: row.targetLabel,
            visibility: this.$i18next.t(
              'tracking:ADMINISTRATOR.activity.UPDATE_DIRECTORY_INTERNAL_CORRESPONDENT.' + visibilityData.visibility
            ),
          };
        }
        break;
      case 'UPDATE_ACTIVATION_ALERT':
        let activationData = this.unwrapExtraData(row.extraData);
        if (activationData !== null && activationData.hasOwnProperty('activation')) {
          // remove the CSR familly because not present in i18n file keys
          let targetLabelKey: string = row.targetLabel;
          if (targetLabelKey.indexOf('CSR_') === 0) {
            targetLabelKey = targetLabelKey.substr('CSR_'.length);
          } else if (targetLabelKey.indexOf('CAMPAIGN_') === 0) {
            targetLabelKey = targetLabelKey.substr('CAMPAIGN_'.length);
          } else if (targetLabelKey.indexOf('SKILL_') === 0) {
            targetLabelKey = targetLabelKey.substr('SKILL_'.length);
          } else if (targetLabelKey.indexOf('SUPERVISOR_') === 0) {
            targetLabelKey = targetLabelKey.substr('SUPERVISOR_'.length);
          } else if (targetLabelKey.indexOf('FLOW_') === 0) {
            targetLabelKey = targetLabelKey.substr('FLOW_'.length);
          } else if (targetLabelKey.indexOf('PLANNING_') === 0) {
            targetLabelKey = targetLabelKey.substr('PLANNING_'.length);
          }
          params = {
            activation: this.$i18next.t('tracking:ADMINISTRATOR.activity.UPDATE_ACTIVATION_ALERT.' + activationData.activation),
            label: this.$i18next.t('commons:alert.' + targetLabelKey, { 0: '(...)', 1: '(...)' }),
          };
        }
        break;
      case 'UPDATE_SKILL_WEIGHT':
        let weightData = this.unwrapExtraData(row.extraData);
        if (weightData !== null && weightData.hasOwnProperty('weight') && weightData.hasOwnProperty('domainName')) {
          params = {
            weight: weightData.weight,
            domain: weightData.domainName,
          };
        }
        break;
      case 'UPDATE_ACTIVATION_EXTERNAL_SERVICE':
        let activationESData = this.unwrapExtraData(row.extraData);
        if (activationESData !== null && activationESData.hasOwnProperty('activation')) {
          params = {
            activation: this.$i18next.t(
              'tracking:ADMINISTRATOR.activity.UPDATE_ACTIVATION_EXTERNAL_SERVICE.' + activationESData.activation
            ),
          };
        }
        break;
      case 'UPDATE_STATUS_CAMPAIGN':
        let campaignStatusData = this.unwrapExtraData(row.extraData);
        if (campaignStatusData !== null && campaignStatusData.hasOwnProperty('campaignStatus')) {
          let campaignStatusLabel = this.$i18next.t(
            'tracking:SUPERVISOR.activity.UPDATE_STATUS_CAMPAIGN.' + campaignStatusData.campaignStatus
          );
          // if status exist
          if (campaignStatusLabel !== 'tracking:SUPERVISOR.activity.UPDATE_STATUS_CAMPAIGN.' + campaignStatusData.campaignStatus) {
            params = {
              status: campaignStatusLabel,
            };
          } else {
            params = {
              status: this.$i18next.t('tracking:SUPERVISOR.activity.UPDATE_STATUS_CAMPAIGN.DEFAULT', {
                raw_status: campaignStatusData.campaignStatus,
              }),
            };
          }
        }
        break;
      case 'ACCEPT_PAUSE':
      case 'REFUSE_PAUSE':
        params = {
          label: row.targetLabel,
        };
        break;
      case 'SET_AVAILABLE_CSR':
        let csr = this.tracking.findCsrFromUuid(row.targetId);
        if (csr !== null) {
          params = {
            label: csr.firstName + ' ' + csr.lastName,
          };
        } else {
          params = {
            label: this.$i18next.t('tracking:object.unknown'),
          };
        }
        break;
      case 'TRANSFER_TO_TEAM':
        params = {
          team: row.targetLabel,
        };
        break;
      default:
        break;
    }
    return this.tracking.getActionText(this.tracking.getFunction(), row.action, row.targetType, params);
  }

  public buildDescriptionText(row) {
    let descriptionText;
    // in case of redirection we got skills ids in target id
    if (
      (row.action === 'REDIRECT' && row.targetType === 'CONTACT') ||
      ((row.action === 'ADD' || row.action === 'REMOVE') && row.targetType === 'SKILL')
    ) {
      // we have to replace skills id by skills names
      let newLabel;
      let skillIds: any = row.targetId;
      let skill;
      if (skillIds !== null) {
        if (isArray(skillIds)) {
          skillIds.forEach(skillId => {
            skill = this.tracking.findSkillFromUuid(skillId);
            if (skill !== null) {
              newLabel = newLabel === null ? skill.name : newLabel + ', ' + skill.name;
            }
          });
        } else if (isString(skillIds)) {
          skill = this.tracking.findSkillFromUuid(skillIds);
          if (skill !== null) {
            newLabel = skill.name;
          }
        }
      }
      descriptionText = this.tracking.getDescriptionText(this.tracking.getFunction(), row.action, row.targetType, newLabel);
    } else {
      descriptionText = this.tracking.getDescriptionText(this.tracking.getFunction(), row.action, row.targetType, row.targetLabel);
    }
    if (row.targetLabel === 'DEFAULT_SIMPLE_PAUSE') {
      descriptionText = descriptionText.replace('DEFAULT_SIMPLE_PAUSE', this.$i18next.t('tracking:object.DEFAULT_SIMPLE_PAUSE'));
    } else if (row.targetLabel === 'DEFAULT_SIMPLE_TASK') {
      descriptionText = descriptionText.replace('DEFAULT_SIMPLE_TASK', this.$i18next.t('tracking:object.DEFAULT_SIMPLE_TASK'));
    }

    return descriptionText;
  }

  public getColumnContent(row, column) {
    let result;
    switch (column.name) {
      case 'dateTime':
        result = this.$filter('date')(row['@timestamp'], 'dd/MM/yyyy|HH:mm:ss');
        break;
      case 'user':
        let user = row.hasOwnProperty('userId') ? this.tracking.findUserFromUuid(row.userId) : null;
        if (user !== null) {
          result = user.firstName + ' ' + user.lastName;
        } else {
          result = this.$i18next.t('tracking:user.unknown');
        }
        break;
      case 'object':
        result = this.buildObjectText(row);
        break;
      case 'action':
        result = this.buildActionText(row);
        break;
      case 'description':
        result = this.buildDescriptionText(row);
        break;
      case 'activity':
        result = this.tracking.getCsrActivityText(row.action, row.targetType);
        break;
      default:
        result = '-';
        break;
    }
    return result;
  }

  public mustDisplayContent(row, column) {
    return (
      column.name === 'action' &&
      row.hasOwnProperty('targetType') &&
      row.targetType === 'ALERT_TEMPLATE' &&
      row.hasOwnProperty('extraData') &&
      row.extraData.hasOwnProperty('alert') &&
      row.extraData.alert.hasOwnProperty('message') &&
      row.extraData.alert.hasOwnProperty('severity')
    );
  }

  public getAlertContent(row) {
    if (row.hasOwnProperty('extraData') && row.extraData.hasOwnProperty('alert')) {
      let tmpSeverity = '';
      let tmpMessage = '';
      if (row.extraData.alert.hasOwnProperty('severity')) {
        tmpSeverity = '[' + row.extraData.alert.severity + '] ';
      }
      if (row.extraData.alert.hasOwnProperty('message')) {
        tmpMessage = row.extraData.alert.message.replace('%@%', ' ');
      }
      return tmpSeverity + tmpMessage;
    }
    return '';
  }

  public queryElements() {
    if (!this.oneSearchDone) {
      this.oneSearchDone = true;
    }
    this.searchInProgress = true;
    this.tracking
      .queryTrackingElements()
      .then(elements => {
        this.rows = elements;
      })
      .finally(() => {
        this.searchInProgress = false;
      });
  }

  public resetFilters() {
    this.tracking.resetFilters();
    this.messaging.forLocal().publish(RESET_TRACKING_FILTER, true);
    this.tracking.getUpdatedHistoryRetention().then(retentionValue => {
      this.initDateTimes(retentionValue);
    });
  }

  // Change to arrow function to keep context when called in tracking header component
  // prettier-ignore
  public changeView = (newView: UserTrackingFunction) => {
    if (this.oneSearchDone) {
      this.oneSearchDone = false;
      this.rows = [];
    }
    if (newView === UserTrackingFunction.AGENT) {
      this.columns = this.agentColumns;
    } else {
      this.columns = this.basicColumns;
    }
    this.selectDefaultColumn();
  };

  public selectDefaultColumn() {
    this.$log.debug('[tracking component] selectDefaultColumn maxChecked:' + this.maxChecked);
    this.maxChecked = this.columns.length;
    this.checked = 0;
    this.columns.forEach(column => {
      // keep selected selected
      if (column.displayed) {
        if (this.checked >= this.maxChecked) {
          column.displayed = false;
        } else {
          this.checked++;
        }
      }
    });
    // select other not selected for fill space
    if (this.checked < this.maxChecked) {
      this.columns.forEach(column => {
        if (!column.displayed) {
          if (this.checked < this.maxChecked) {
            column.displayed = true;
            this.checked++;
          } else {
            column.displayed = false;
          }
        }
      });
    }
  }

  public updateBeginDate() {
    this.beginDateTime.setHours(this.beginHours);
    this.beginDateTime.setMinutes(this.beginMinutes);
    this.tracking.updateBeginDate(this.beginDateTime);
  }

  public updateEndDate() {
    this.endDateTime.setHours(this.endHours);
    this.endDateTime.setMinutes(this.endMinutes);
    this.tracking.updateEndDate(this.endDateTime);
  }

  private initDateTimes(updatedHistoryRetention) {
    // begin date time
    let d = new Date();
    this.todayDateTime = new Date();

    this.beginHours = 0;
    this.beginMinutes = 0;
    d.setHours(this.beginHours);
    d.setMinutes(this.beginMinutes);
    d.setSeconds(0);
    d.setMilliseconds(0);
    this.beginDateTime = d;
    this.tracking.updateBeginDate(this.beginDateTime);

    // end date time
    this.endDateTime = new Date();
    this.endHours = this.endDateTime.getHours();
    this.endMinutes = this.endDateTime.getMinutes();
    this.endDateTime.setSeconds(0);
    this.endDateTime.setMilliseconds(0);
    this.tracking.updateEndDate(this.endDateTime);

    // min date is calculated from updatedHistoryRetention
    this.minDate = new Date();
    this.minDate.setDate(this.minDate.getDate() - updatedHistoryRetention);

    // max date is today
    this.maxDate = new Date();
  }

  public enableMetric(column) {
    if (column.displayed) this.checked++;
    else this.checked--;
  }

  updateBeginHoursDate(newValue) {
    this.beginDateTime.setHours(newValue);
    this.tracking.updateBeginDate(this.beginDateTime);
  }

  updateBeginMinutesDate(newValue) {
    this.beginDateTime.setMinutes(newValue);
    this.tracking.updateBeginDate(this.beginDateTime);
  }

  updateEndHoursDate(newValue) {
    this.endDateTime.setHours(newValue);
    this.tracking.updateEndDate(this.endDateTime);
  }

  updateEndMinutesDate(newValue) {
    this.endDateTime.setMinutes(newValue);
    this.tracking.updateEndDate(this.endDateTime);
  }

  public resetDateTimes() {
    this.tracking.getUpdatedHistoryRetention().then(retentionValue => {
      this.initDateTimes(retentionValue);
    });
  }

  public openBeginDateTime() {
    this.status.openedBeginDate = true;
  }

  public openEndDateTime() {
    this.status.openedEndDate = true;
  }

  public actionDetailReceived(updateData) {
    let after;
    if (updateData.hasOwnProperty('after')) {
      after = fromJson(updateData.after);
    }
    let before;
    if (updateData.hasOwnProperty('before')) {
      before = fromJson(updateData.before);
    }
    if (Object.keys(after).length > 0) {
      // in case of create uuid is not valued so don't use it in diff
      if (after.hasOwnProperty('uuid')) {
        delete after.uuid;
      }
      let delta;
      let jsondiffpatch = new DiffPatcher();
      if (Object.keys(before).length > 0) {
        // in case of create uuid is not valued so don't use it in diff
        if (before.hasOwnProperty('uuid')) {
          delete before.uuid;
        }
        delta = jsondiffpatch.diff(before, after);
        this.updateActionText = this.$i18next.t('commons:basic.action');
        this.updateData = this.$sce.trustAsHtml(formatters.html.format(delta, before));
      } else {
        delta = jsondiffpatch.diff(updateData.after, {});
        this.updateActionText = this.$i18next.t('tracking:diff.noDataBeforeFound');
        this.updateData = this.$sce.trustAsHtml(formatters.html.format(delta, updateData.after));
      }
    } else {
      this.updateActionText = this.$i18next.t('tracking:diff.noDataFound');
      this.updateData = this.$sce.trustAsHtml('');
    }
  }

  public displayActionDetail(row) {
    let updateDataId = row.hasOwnProperty('extraData') ? this.extractUpdateDataId(fromJson(row.extraData)) : null;
    if (updateDataId !== null) {
      this.tracking.getUpdateData(updateDataId).then(data => {
        this.updateTime = this.getColumnContent(row, { name: 'dateTime' });
        this.updatedObject = this.getColumnContent(row, { name: 'object' });
        this.updateInitiator = this.getColumnContent(row, { name: 'user' });
        this.actionInfoState = true;
        this.actionDetailReceived(data);
      });
    }
  }

  public rowHasDetail(row): boolean {
    try {
      return row.hasOwnProperty('extraData') && this.extractUpdateDataId(fromJson(row.extraData)) !== null;
    } catch (e) {
      if (row.hasOwnProperty('message')) {
        this.$log.warn('Error occurred in rowHasDetail for row[' + row.message + ']', e);
      } else {
        this.$log.warn('Error occurred in rowHasDetail', e);
      }
    }

    return false;
  }

  public closeActionDetail() {
    this.actionInfoState = false;
    this.updateData = '';
    this.updateTime = '';
    this.updatedObject = '';
    this.updateInitiator = '';
  }

  public changeColor(color) {
    this.applicationColor = color;
  }

  public moveAction(position) {
    this.actionPosition = position;
  }

  public closeAction() {
    this.actionStateClass = '';
  }

  public toggleAction() {
    this.actionState = !this.actionState;
    if (this.actionState) {
      this.actionStateClass = 'expanded';
    } else {
      this.actionStateClass = '';
    }
  }
}

export class UserTrackingTable implements IComponentOptions {
  templateUrl = templateUrl;
  transclude = {
    filterRef: '?filterRef',
  };
  bindings = {
    trackingFn: '<',
  };
  controller = ['$log', '$i18next', '$filter', '$parse', '$sce', '$window', 'messaging', 'tracking', UserTrackingTableCtrl];
}

export default 'contactUserTableTracking';
